import {gaConfig} from '@stencil/hotels-config';
import {
  hotelPolicyAdditionalDataSelector,
  hotelsAmenitiesSelector,
  isStaticDataLoading as isStaticDataLoadingSelectorFn,
} from '@stencil/hotels-details-reducer/src/selectors';
import detailsPageClickGaPush from '@stencil/hotels-utils/src/details-page-click-ga-push';
import {
  detailsNavigationIds,
  detailsNavigationIdsAndValues,
} from '@stencil/hotels-utils/src/get-details-navigation-id-and-values';
import {scrollIntoViewDetails} from '@stencil/hotels-utils/src/scroll-into-view-details';
import {defaultTheme} from '@stencil/build-theme';
import React, {Fragment, useRef, useState, useEffect} from 'react';
import {useSelector} from 'react-redux';

import ThingsYouShouldKnow from '../../../../../stories/components/hotels/DetailsPage/ThingsYouShouldKnow/ThingsYouShouldKnow';
import AmenitiesDetailsLoader from '../../../../../stories/components/hotels/Loadingpage/AmenitiesDetailsLoader/AmenitiesDetailsLoader';
import AmenitiesIconSelector from '../../../../components/AmenitiesIconSelector';
import {PrimaryLink} from '../../../../../stories/components/CommonStyleUtils/dwebCommon.styles';
import {openCategorizedAmenitiesSelector} from '@stencil/hotels-details-reducer/src/selectors';
import * as S from './Amenities.styles';

// SELECTORS
// HOOKS
// UTILS
// COMPONENTS
// COLOR Palette
const {DetailsGAClickEventActions} = gaConfig;

const ListOfCategoryAmenities: React.FC<{amenities: string[]}> = ({amenities}) => {
  return (
    <S.OneCategoryOfAmenitiesList>
      {amenities.map(amenityName => (
        <Fragment key={amenityName}>
          <S.OneCategoryOfAmenitiesListItem key={amenityName}>
            <S.AmenityInnerItemIconStylingSpan>
              <AmenitiesIconSelector
                name={amenityName}
                fill={defaultTheme.colors.downriver.medium}
                heights="1.5rem"
                width="1.5rem"
              />
            </S.AmenityInnerItemIconStylingSpan>
            <S.AmenityInnerItemTextStylingSpan>{amenityName}</S.AmenityInnerItemTextStylingSpan>
          </S.OneCategoryOfAmenitiesListItem>
        </Fragment>
      ))}
    </S.OneCategoryOfAmenitiesList>
  );
};
const Amenities = () => {
  const amenitiesRef = useRef(null);
  const openCategorizedAmenities = useSelector(openCategorizedAmenitiesSelector);
  const [moreAmenitiesShowState, setMoreAmenitiesShowState] = useState(false);
  const isInitialRender = useRef(false);

  const additionalHotelPoliciesData = useSelector(hotelPolicyAdditionalDataSelector);

  const amentiesData = useSelector(hotelsAmenitiesSelector);

  const {
    all: amenitiesList,
    cmapped: cmappedAmenitiesList,
    categorized: categorizedAmenitiesList,
  } = amentiesData;

  const cmappedfourAmenitiesList = cmappedAmenitiesList.slice(0, 4);

  const showMore = categorizedAmenitiesList.length;

  useEffect(() => {
    if (isInitialRender.current == false) isInitialRender.current = true;
    else setMoreAmenitiesShowState(true);
  }, [openCategorizedAmenities]);

  const isStaticDataLoading = useSelector(isStaticDataLoadingSelectorFn);

  if (isStaticDataLoading) {
    return (
      <S.AmenitiesWrapper ref={amenitiesRef} id={detailsNavigationIds.amenities}>
        <AmenitiesDetailsLoader />
      </S.AmenitiesWrapper>
    );
  }

  if (!amenitiesList.length) {
    return null;
  }

  return (
    <S.AmenitiesWrapper ref={amenitiesRef} id={detailsNavigationIds.amenities}>
      {/* AMENITIES HEAD */}

      <S.AmenitiesHeaderStyled>Amenities</S.AmenitiesHeaderStyled>

      <S.AmenitiesListBlock>
        {/* POPULAR AMENTITIES STATIC LABEL */}
        <S.PopularAmenitiesTag>
          <S.PopularAmenitiesTagTextStyled>POPULAR AMENITIES</S.PopularAmenitiesTagTextStyled>
        </S.PopularAmenitiesTag>

        {/* AMENITIES  */}
        <S.AmentiesHorizontalListof4>
          {cmappedfourAmenitiesList.map((amenityDetail, index) => (
            <Fragment key={amenityDetail?.n || index}>
              <AmenitiesIconSelector
                name={amenityDetail?.n || ''}
                styleProps={{
                  fill: defaultTheme.colors.goGreen.base,
                }}
              />
              <S.AmenityItemText>{amenityDetail?.n || ''}</S.AmenityItemText>
            </Fragment>
          ))}

          {!moreAmenitiesShowState && (
            <PrimaryLink
              onClick={e => {
                e.preventDefault();

                detailsPageClickGaPush({
                  eventAction: DetailsGAClickEventActions.amenities.amenitiesViewAllClick,
                });

                // Toggle more amenities show state
                setMoreAmenitiesShowState(true);
              }}
            >
              View More
            </PrimaryLink>
          )}
        </S.AmentiesHorizontalListof4>
      </S.AmenitiesListBlock>

      {moreAmenitiesShowState && (
        <S.CategorizedAmenitiesTableContainer>
          {categorizedAmenitiesList.map(oneCategoryAmenities => (
            <S.OneCateoryOfAmenitiesContainer
              key={oneCategoryAmenities.label}
              id={oneCategoryAmenities.label}
            >
              <S.HeadingOneCategoryOfAmenitiesBlockStyled>
                {oneCategoryAmenities.label}
              </S.HeadingOneCategoryOfAmenitiesBlockStyled>

              <ListOfCategoryAmenities amenities={oneCategoryAmenities.data || []} />
            </S.OneCateoryOfAmenitiesContainer>
          ))}
        </S.CategorizedAmenitiesTableContainer>
      )}

      {moreAmenitiesShowState && (
        <S.HideAllAmenitiesTextContainer>
          <S.HideAllAmenitiesText
            href="#"
            onClick={e => {
              e.preventDefault();

              detailsPageClickGaPush({
                eventAction: DetailsGAClickEventActions.amenities.amenitiesViewLessClick,
              });

              setMoreAmenitiesShowState(false);

              // reset user's view to bring it to amenities container
              scrollIntoViewDetails(
                document.querySelector(detailsNavigationIdsAndValues.amenities.id),
                {
                  duration: 500,
                },
              );
            }}
          >
            Hide
          </S.HideAllAmenitiesText>
        </S.HideAllAmenitiesTextContainer>
      )}

      <ThingsYouShouldKnow list={additionalHotelPoliciesData || []} />
    </S.AmenitiesWrapper>
  );
};

export default Amenities;
